// Phase 4 — creative-assets batch UI: pure grouping helpers.
//
// Proposals sharing a non-null `batch_id` collapse into one batch entry in
// the Action Queue; everything else renders as a plain row exactly as before.
// Kept free of React so it can be unit-tested in isolation.
import type {
  CreativePreview,
  Proposal,
} from '../../../lib/api/proposals/proposals-api';

export interface BatchQueueEntry {
  kind: 'batch';
  batchId: string;
  proposals: Proposal[];
}

export interface SingleQueueEntry {
  kind: 'single';
  proposal: Proposal;
}

export type QueueEntry = BatchQueueEntry | SingleQueueEntry;

/**
 * Group a page of proposals by `batch_id`, preserving list order.
 *
 * - The first proposal seen for a batch anchors the batch's position.
 * - Proposals with a null/undefined/empty `batch_id` stay single entries.
 * - A "batch" of one still renders as a batch (it IS one server-side), so
 *   batch actions and the deep link stay consistent across page boundaries.
 */
export function groupProposalsByBatch(proposals: Proposal[]): QueueEntry[] {
  const entries: QueueEntry[] = [];
  const batchIndex = new Map<string, BatchQueueEntry>();

  for (const proposal of proposals) {
    const batchId = proposal.batch_id;
    if (!batchId) {
      entries.push({ kind: 'single', proposal });
      continue;
    }
    const existing = batchIndex.get(batchId);
    if (existing) {
      existing.proposals.push(proposal);
    } else {
      const entry: BatchQueueEntry = {
        kind: 'batch',
        batchId,
        proposals: [proposal],
      };
      batchIndex.set(batchId, entry);
      entries.push(entry);
    }
  }

  return entries;
}

/**
 * Collect up to `limit` creative previews across a batch's items, in item
 * order. Used for the batch card's thumbnail strip.
 */
export function collectCreativePreviews(
  proposals: Proposal[],
  limit = 4
): CreativePreview[] {
  const previews: CreativePreview[] = [];
  for (const proposal of proposals) {
    for (const preview of proposal.creative_previews ?? []) {
      previews.push(preview);
      if (previews.length >= limit) return previews;
    }
  }
  return previews;
}

export interface BatchStatusRollup {
  total: number;
  pending: number;
  approved: number;
  denied: number;
  failed: number;
  other: number;
}

/** Client-side status rollup for the chips on the batch card. */
export function rollupBatchStatuses(proposals: Proposal[]): BatchStatusRollup {
  const rollup: BatchStatusRollup = {
    total: proposals.length,
    pending: 0,
    approved: 0,
    denied: 0,
    failed: 0,
    other: 0,
  };
  for (const p of proposals) {
    switch (p.status) {
      case 'proposed':
      case 'pending_review':
        rollup.pending += 1;
        break;
      case 'approved':
      case 'executing':
      case 'completed':
        rollup.approved += 1;
        break;
      case 'denied':
        rollup.denied += 1;
        break;
      case 'failed':
        rollup.failed += 1;
        break;
      default:
        rollup.other += 1;
        break;
    }
  }
  return rollup;
}

/** Unique, order-preserving list of platform slugs across a batch. */
export function batchPlatforms(proposals: Proposal[]): string[] {
  const seen = new Set<string>();
  const platforms: string[] = [];
  for (const p of proposals) {
    if (p.platform && !seen.has(p.platform)) {
      seen.add(p.platform);
      platforms.push(p.platform);
    }
  }
  return platforms;
}

/** Unique, order-preserving list of shop display labels across a batch. */
export function batchShopLabels(
  proposals: Proposal[],
  labelOf: (p: Proposal) => string
): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const p of proposals) {
    const label = labelOf(p);
    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  return labels;
}
